import React, { useCallback, useState, useEffect, useRef } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, deleteDoc, doc, getDoc, getDocs, onSnapshot, query, setDoc, serverTimestamp, Timestamp, where } from 'firebase/firestore';
import { db } from '../firebase';
import { bingoPatterns, getBingoPattern, getPatternLabel } from '../data/bingoPatterns';
import { createEventScheduleFromTemplate } from '../utils/programTemplateUtils';
import { appendAuditEvent } from '../utils/auditTrail';
import './BingoMachine.css';
import './BingoMachineCallerTools.css';
import './BingoMachineExtraThemes.css';

const DEFAULT_DISPLAY_CONTROL = {
  mode: 'live',
  announcementTitle: '',
  announcementMessage: '',
  intermissionMessage: '',
};

const DISPLAY_MODES = ['live', 'upNext', 'intermission', 'announcement', 'standby'];

const normalizeDisplayControl = (value) => ({
  mode: DISPLAY_MODES.includes(value?.mode) ? value.mode : 'live',
  announcementTitle: typeof value?.announcementTitle === 'string'
    ? value.announcementTitle
    : '',
  announcementMessage: typeof value?.announcementMessage === 'string'
    ? value.announcementMessage
    : '',
  intermissionMessage: typeof value?.intermissionMessage === 'string'
    ? value.intermissionMessage
    : '',
  ...(value?.updatedAt ? { updatedAt: value.updatedAt } : {}),
});

function AutoCallCountdown({ active, nextCallAt }) {
  const [remaining, setRemaining] = useState(() => (
    active && nextCallAt
      ? Math.max(0, Math.ceil((nextCallAt - Date.now()) / 1000))
      : null
  ));

  useEffect(() => {
    if (!active || !nextCallAt) {
      setRemaining(null);
      return undefined;
    }

    const updateRemaining = () => setRemaining(
      Math.max(0, Math.ceil((nextCallAt - Date.now()) / 1000))
    );
    updateRemaining();
    const intervalId = setInterval(updateRemaining, 250);
    return () => clearInterval(intervalId);
  }, [active, nextCallAt]);

  if (remaining === null) return null;

  return (
    <div className={`machine-auto-call-countdown ${remaining <= 3 ? 'ending' : ''}`} role="timer" aria-live="off">
      <span>NEXT BALL IN</span>
      <strong>00:{String(remaining).padStart(2, '0')}</strong>
    </div>
  );
}

function BingoMachine({ isNavbarVisible, setIsNavbarVisible }) {
  const [generatedNumbers, setGeneratedNumbers] = useState([]);
  const [currentBall, setCurrentBall] = useState(null); // Current ball being "drawn"
  const [nextBall, setNextBall] = useState(null);
  const [isMachineRunning, setIsMachineRunning] = useState(false); // Track if the machine is running
  const [selectedPattern, setSelectedPattern] = useState(null); // Track the selected winning pattern
  const [prizeAmount, setPrizeAmount] = useState(''); // Track the prize amount
  const [showConfirm, setShowConfirm] = useState(false); // Show reset confirmation modal
  const [showIntro, setShowIntro] = useState(true); // Show intro splash on page load
  const [introFading, setIntroFading] = useState(false);
  const [animatingNumber, setAnimatingNumber] = useState(null); // Number currently blinking on flashboard
  const [manualMode, setManualMode] = useState(false); // Manual mode toggle
  const [gameNumber, setGameNumber] = useState(1); // Which game number (min 1)
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('flashboardTheme') || 'standard';
    } catch (e) {
      return 'standard';
    }
  });
  const [eventNameInput, setEventNameInput] = useState('');
  const [isSessionStarted, setIsSessionStarted] = useState(false);
  const [sessionDocId, setSessionDocId] = useState('');
  const [isStartingSession, setIsStartingSession] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [userEvents, setUserEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState('');
  const [programTemplates, setProgramTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const [controlView, setControlView] = useState('dashboard');
  const [displayControl, setDisplayControl] = useState(DEFAULT_DISPLAY_CONTROL);
  const [calledBallOverlay, setCalledBallOverlay] = useState(null);
  const isApplyingRemoteRef = useRef(false);
  const pendingUndoRef = useRef(null);
  const pendingCallerRuntimeRef = useRef(null);
  const calledBallOverlayTimeoutRef = useRef(null);
  const undoMessageTimeoutRef = useRef(null);
  const previousGeneratedNumbersRef = useRef(null);
  const requestedEventOpenedRef = useRef('');
  const [gameSchedule, setGameSchedule] = useState([]);
  const [activeGameIndex, setActiveGameIndex] = useState(-1);
  const [winnerCalled, setWinnerCalled] = useState(false);
  const [undoMessage, setUndoMessage] = useState('');
  const [showWinner, setShowWinner] = useState(false);
  const [confettiPieces, setConfettiPieces] = useState([]);
  const [fireworksPieces, setFireworksPieces] = useState([]);
  const [autoCallEnabled, setAutoCallEnabled] = useState(false);
  const [autoCallInterval, setAutoCallInterval] = useState(10);
  const [autoCallNextAt, setAutoCallNextAt] = useState(null);
  const generatedNumbersRef = useRef([]);
  const nextBallRef = useRef(null);
  const autoCallEnabledRef = useRef(false);
  const autoCallIntervalRef = useRef(10);
  const autoCallTimerRef = useRef(null);
  const autoCallNextAtRef = useRef(null);
  const manualCallTimerRef = useRef(null);
  const callAnimationTimerRef = useRef(null);
  const commitInProgressRef = useRef(false);
  const callingContextRef = useRef('');

  // Custom pattern (5x5) for user-defined selections
  const [customPattern, setCustomPattern] = useState(() =>
    Array.from({ length: 5 }, () => Array.from({ length: 5 }, () => false))
  );

  const maxNumber = 75; // Fixed for a true bingo flashboard (1–75)
  const rows = 5; // Number of rows
  const numbersPerRow = 15; // Numbers per row
  const bingoLetters = ['B', 'I', 'N', 'G', 'O']; // Bingo column labels

  const isPatternCellActive = (row, column) => {
    if (selectedPattern === 'custom') {
      return Boolean(customPattern?.[row]?.[column]);
    }

    const pattern = getBingoPattern(selectedPattern);
    return Boolean(pattern?.masks?.[0]?.[row]?.[column]);
  };

  const resetMachine = () => {
    stopAutoCall();
    cancelPendingCall(true);
    persistCallerRuntime(
      [],
      null,
      null,
      {
        selectedPattern: null,
        prizeAmount: '',
        winnerCalled: false,
        showWinner: false,
      },
      {
        pattern: null,
        prizeAmount: '',
      }
    );
    setGeneratedNumbers([]);
    generatedNumbersRef.current = [];
    setCurrentBall(null);
    setIsMachineRunning(false);
    setSelectedPattern(null); // Reset the selected pattern
    setPrizeAmount(''); // Reset the prize amount
    setWinnerCalled(false);
    setShowWinner(false);
    setUndoMessage('');
  };

  const toggleCustomCell = (r, c) => {
    setCustomPattern((prev) => {
      const copy = prev.map((row) => row.slice());
      copy[r][c] = !copy[r][c];
      return copy;
    });
  };

  const openResetConfirm = () => {
    stopAutoCall();
    cancelPendingCall(false);
    setShowConfirm(true);
  };
  const closeResetConfirm = () => setShowConfirm(false);
  const confirmReset = () => {
    resetMachine();
    closeResetConfirm();
  };

  const callWinner = () => {
    if (showWinner || winnerCalled) return;
    stopAutoCall();
    cancelPendingCall(false);
    setWinnerCalled(true);
    const colors = ['#ff4757', '#ff6b6b', '#ffd166', '#7bed9f', '#70a1ff', '#9980FA'];
    const pieces = Array.from({ length: 80 }).map((_, i) => ({
      id: `c-${Date.now()}-${i}`,
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 0.6}s`,
      duration: `${2 + Math.random() * 2}s`,
      bg: colors[Math.floor(Math.random() * colors.length)],
      rotate: `${Math.floor(Math.random() * 360)}deg`,
    }));

    // Fireworks: create a few explosions near the top half
    const fw = Array.from({ length: 6 }).map((_, i) => ({
      id: `f-${Date.now()}-${i}`,
      left: `${10 + Math.random() * 80}%`,
      top: `${6 + Math.random() * 24}%`,
      delay: `${0.1 + Math.random() * 1.2}s`,
      color: colors[Math.floor(Math.random() * colors.length)],
      particleCount: 10 + Math.floor(Math.random() * 10),
    }));

    setConfettiPieces(pieces);
    setFireworksPieces(fw);
    setShowWinner(true);
    appendAuditEvent({
      eventId: sessionDocId,
      game: gameSchedule[activeGameIndex] || {
        id: `game-${gameNumber}`,
        gameNumber,
        name: `Game ${gameNumber}`,
        pattern: selectedPattern,
        prizeAmount,
      },
      type: 'WINNER_CONFIRMED',
      payload: {
        prizeAmount,
        calledBallCount: generatedNumbersRef.current.length,
        source: 'operator',
      },
      idempotencyKey: `winner-confirmed-${gameSchedule[activeGameIndex]?.id || gameNumber}`,
    });
    setTimeout(() => setShowWinner(false), 6000);
    setTimeout(() => {
      setConfettiPieces([]);
      setFireworksPieces([]);
    }, 6500);
  };

  useEffect(() => {
    // Start fade after 2s, remove overlay after 2.5s
    const fadeTimer = setTimeout(() => setIntroFading(true), 2000);
    const hideTimer = setTimeout(() => setShowIntro(false), 2500);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  // Persist theme selection
  useEffect(() => {
    try {
      localStorage.setItem('flashboardTheme', theme);
    } catch (e) {
      // ignore
    }
  }, [theme]);

  useEffect(() => () => {
    if (calledBallOverlayTimeoutRef.current) {
      clearTimeout(calledBallOverlayTimeoutRef.current);
    }

    if (undoMessageTimeoutRef.current) {
      clearTimeout(undoMessageTimeoutRef.current);
    }

    if (autoCallTimerRef.current) {
      clearTimeout(autoCallTimerRef.current);
    }

    if (manualCallTimerRef.current) {
      clearTimeout(manualCallTimerRef.current);
    }

    if (callAnimationTimerRef.current) {
      clearTimeout(callAnimationTimerRef.current);
    }

    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }

    autoCallEnabledRef.current = false;
    autoCallNextAtRef.current = null;
    commitInProgressRef.current = false;
    pendingCallerRuntimeRef.current = null;
  }, []);

  useEffect(() => {
    generatedNumbersRef.current = generatedNumbers;
  }, [generatedNumbers]);

  useEffect(() => {
    nextBallRef.current = nextBall;
  }, [nextBall]);

  useEffect(() => {
    autoCallIntervalRef.current = autoCallInterval;
  }, [autoCallInterval]);

  useEffect(() => {
    if (manualMode || winnerCalled || showWinner) {
      stopAutoCall();
      cancelPendingCall(false);
    }
    // Timer controls intentionally use refs so this effect reacts only to lifecycle state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualMode, winnerCalled, showWinner]);

  useEffect(() => {
    const callingContext = `${sessionDocId}:${activeGameIndex}`;

    if (
      callingContextRef.current &&
      callingContextRef.current !== callingContext
    ) {
      stopAutoCall();
      cancelPendingCall(false);
    }

    callingContextRef.current = callingContext;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionDocId, activeGameIndex]);

  useEffect(() => {
    const activeScheduledGame = gameSchedule[activeGameIndex];
    if (
      activeGameIndex >= 0 &&
      activeScheduledGame?.status !== 'active'
    ) {
      stopAutoCall();
      cancelPendingCall(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameSchedule, activeGameIndex]);

  const startMachine = () => {
    if (manualMode || autoCallEnabledRef.current) return;
    if (
      isMachineRunning ||
      generatedNumbersRef.current.length >= maxNumber ||
      !selectedPattern ||
      !prizeAmount ||
      Number(gameNumber) < 1
    ) return;

    if (nextBallRef.current) {
      commitNextBall();
      return;
    }

    const stagedBall = stageNextBall();
    if (!stagedBall) return;

    setIsMachineRunning(true);
    manualCallTimerRef.current = setTimeout(() => {
      manualCallTimerRef.current = null;
      commitNextBall();
    }, 2500);
  };

  // Helper function to determine the letter for a number
  const getLetterForNumber = (number) => {
    if (number >= 1 && number <= 15) return 'B';
    if (number >= 16 && number <= 30) return 'I';
    if (number >= 31 && number <= 45) return 'N';
    if (number >= 46 && number <= 60) return 'G';
    if (number >= 61 && number <= 75) return 'O';
    return '';
  };

  // Helper to generate a unique bingo number not already drawn
  const generateUniqueNumber = () => {
    const existing = new Set(
      generatedNumbersRef.current.map((ball) => Number(ball.number))
    );

    if (nextBallRef.current?.number) {
      existing.add(Number(nextBallRef.current.number));
    }

    if (existing.size >= maxNumber) return null;

    let n;
    do {
      n = Math.floor(Math.random() * maxNumber) + 1;
    } while (existing.has(n));
    return n;
  };

  function normalizeStagedBall(ball, calledBalls) {
    const number = Number(ball?.number);
    if (
      !Number.isInteger(number) ||
      number < 1 ||
      number > maxNumber ||
      calledBalls.some(
        (calledBall) => Number(calledBall.number) === number
      )
    ) {
      return null;
    }

    return {
      letter: ball.letter || getLetterForNumber(number),
      number,
    };
  }

  function getCallerRuntimeKey(calledBalls, calledBall, upcomingBall) {
    return JSON.stringify({
      numbers: calledBalls.map((ball) => Number(ball.number)),
      currentBall: calledBall || null,
      nextBall: upcomingBall?.number ? Number(upcomingBall.number) : null,
    });
  }

  function persistCallerRuntime(
    calledBalls,
    calledBall,
    upcomingBall,
    eventOverrides = {},
    scheduleOverrides = {}
  ) {
    if (!isSessionStarted || !sessionDocId) return;

    const runtimeKey = getCallerRuntimeKey(
      calledBalls,
      calledBall,
      upcomingBall
    );
    pendingCallerRuntimeRef.current = runtimeKey;

    const synchronizedSchedule = gameSchedule.map((game, index) =>
      index === activeGameIndex && game.status !== 'completed'
        ? {
            ...game,
            generatedNumbers: calledBalls,
            currentBall: calledBall,
            nextBall: upcomingBall,
            ...scheduleOverrides,
          }
        : game
    );

    // Keep the caller UI and the active scheduled game aligned immediately.
    // The Firestore listener will later confirm this same state.
    setGameSchedule(synchronizedSchedule);

    setDoc(
      doc(db, 'bingoMachineEvents', sessionDocId),
      {
        generatedNumbers: calledBalls,
        currentBall: calledBall,
        nextBall: upcomingBall,
        gameSchedule: synchronizedSchedule,
        ...eventOverrides,
        lastUpdated: serverTimestamp(),
      },
      { merge: true }
    ).catch((error) => {
      if (pendingCallerRuntimeRef.current === runtimeKey) {
        pendingCallerRuntimeRef.current = null;
      }
      // eslint-disable-next-line no-console
      console.error('Error saving caller runtime:', error);
    });
  }

  function stageNextBall(shouldPersist = true) {
    if (nextBallRef.current) return nextBallRef.current;

    const selectedNumber = generateUniqueNumber();
    if (!selectedNumber) return null;

    const stagedBall = {
      letter: getLetterForNumber(selectedNumber),
      number: selectedNumber,
    };

    nextBallRef.current = stagedBall;
    setNextBall(stagedBall);

    if (shouldPersist) {
      persistCallerRuntime(
        generatedNumbersRef.current,
        currentBall,
        stagedBall
      );
    }

    return stagedBall;
  }

  function scheduleAutoCall(delay = autoCallIntervalRef.current * 1000) {
    if (autoCallTimerRef.current) {
      clearTimeout(autoCallTimerRef.current);
    }

    if (!autoCallEnabledRef.current || !nextBallRef.current) {
      autoCallNextAtRef.current = null;
      setAutoCallNextAt(null);
      return;
    }

    const nextCallAt = Date.now() + delay;
    autoCallNextAtRef.current = nextCallAt;
    setAutoCallNextAt(nextCallAt);
    if (isSessionStarted && sessionDocId) {
      setDoc(
        doc(db, 'bingoMachineEvents', sessionDocId),
        {
          autoCallEnabled: true,
          autoCallInterval: autoCallIntervalRef.current,
          autoCallNextAt: nextCallAt,
          lastUpdated: serverTimestamp(),
        },
        { merge: true }
      ).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error sharing Auto Call timing:', error);
      });
    }

    autoCallTimerRef.current = setTimeout(() => {
      autoCallTimerRef.current = null;
      if (
        autoCallEnabledRef.current &&
        autoCallNextAtRef.current === nextCallAt
      ) {
        autoCallNextAtRef.current = null;
        setAutoCallNextAt(null);
        commitNextBall();
      }
    }, delay);
  }

  function stopAutoCall() {
    const wasActive = autoCallEnabledRef.current || autoCallNextAtRef.current;
    autoCallEnabledRef.current = false;
    setAutoCallEnabled(false);
    autoCallNextAtRef.current = null;
    setAutoCallNextAt(null);

    if (autoCallTimerRef.current) {
      clearTimeout(autoCallTimerRef.current);
      autoCallTimerRef.current = null;
    }

    if (wasActive && isSessionStarted && sessionDocId) {
      setDoc(
        doc(db, 'bingoMachineEvents', sessionDocId),
        {
          autoCallEnabled: false,
          autoCallNextAt: null,
          lastUpdated: serverTimestamp(),
        },
        { merge: true }
      ).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error sharing paused Auto Call state:', error);
      });
    }
  }

  function cancelPendingCall(clearStagedBall) {
    if (manualCallTimerRef.current) {
      clearTimeout(manualCallTimerRef.current);
      manualCallTimerRef.current = null;
    }

    if (callAnimationTimerRef.current) {
      clearTimeout(callAnimationTimerRef.current);
      callAnimationTimerRef.current = null;
    }

    commitInProgressRef.current = false;
    setAnimatingNumber(null);
    setIsMachineRunning(false);

    if (clearStagedBall) {
      nextBallRef.current = null;
      setNextBall(null);
    }
  }

  function commitNextBall() {
    const ball = nextBallRef.current;
    if (!ball || commitInProgressRef.current) return false;

    if (
      generatedNumbersRef.current.some(
        (calledBall) => Number(calledBall.number) === Number(ball.number)
      )
    ) {
      nextBallRef.current = null;
      setNextBall(null);
      const replacementBall = stageNextBall(false);
      persistCallerRuntime(
        generatedNumbersRef.current,
        currentBall,
        replacementBall
      );
      if (autoCallEnabledRef.current && replacementBall) {
        scheduleAutoCall();
      } else if (!replacementBall) {
        stopAutoCall();
      }
      return false;
    }

    commitInProgressRef.current = true;
    nextBallRef.current = null;
    setNextBall(null);

    const updatedNumbers = [
      ...generatedNumbersRef.current,
      ball,
    ];
    generatedNumbersRef.current = updatedNumbers;
    setGeneratedNumbers(updatedNumbers);
    setCurrentBall(`${ball.letter}${ball.number}`);
    setAnimatingNumber(ball.number);
    setIsMachineRunning(true);

    if (callAnimationTimerRef.current) {
      clearTimeout(callAnimationTimerRef.current);
    }

    callAnimationTimerRef.current = setTimeout(() => {
      setAnimatingNumber(null);
      setIsMachineRunning(false);
      callAnimationTimerRef.current = null;
      commitInProgressRef.current = false;
    }, 1500);

    const stagedBall = stageNextBall(false);
    persistCallerRuntime(
      updatedNumbers,
      `${ball.letter}${ball.number}`,
      stagedBall
    );
    appendAuditEvent({
      eventId: sessionDocId,
      game: gameSchedule[activeGameIndex] || {
        id: `game-${gameNumber}`,
        gameNumber,
        name: `Game ${gameNumber}`,
        pattern: selectedPattern,
        prizeAmount,
      },
      type: 'BALL_CALLED',
      payload: {
        ball: `${ball.letter}${ball.number}`,
        callNumber: updatedNumbers.length,
        source: autoCallEnabledRef.current ? 'auto' : 'caller',
      },
    });

    if (autoCallEnabledRef.current && stagedBall) {
      scheduleAutoCall();
    } else if (!stagedBall) {
      stopAutoCall();
    }

    return true;
  }

  const startAutoCall = () => {
    if (
      manualMode ||
      winnerCalled ||
      !selectedPattern ||
      !prizeAmount ||
      Number(gameNumber) < 1 ||
      generatedNumbersRef.current.length >= maxNumber
    ) return;

    if (manualCallTimerRef.current) {
      clearTimeout(manualCallTimerRef.current);
      manualCallTimerRef.current = null;
    }

    setIsMachineRunning(false);
    autoCallEnabledRef.current = true;
    setAutoCallEnabled(true);

    const stagedBall = stageNextBall();
    if (stagedBall) {
      scheduleAutoCall();
    } else {
      stopAutoCall();
    }
  };

  const handleAutoCallIntervalChange = (event) => {
    const seconds = Number(event.target.value);
    autoCallIntervalRef.current = seconds;
    setAutoCallInterval(seconds);

    if (autoCallEnabledRef.current) {
      scheduleAutoCall(seconds * 1000);
    }
  };

  const showCalledBallFlash = useCallback((ball) => {
    setCalledBallOverlay(ball);

    if (calledBallOverlayTimeoutRef.current) {
      clearTimeout(calledBallOverlayTimeoutRef.current);
    }

    calledBallOverlayTimeoutRef.current = setTimeout(() => {
      setCalledBallOverlay(null);
      calledBallOverlayTimeoutRef.current = null;
    }, 5000);
  }, []);

  const speakCalledBall = useCallback((ball) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      return;
    }

    const utterance = new SpeechSynthesisUtterance(`Now calling ${ball.letter} ${ball.number}`);
    utterance.rate = 0.78;
    utterance.pitch = 1.05;
    utterance.volume = 1;

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  }, []);

  const triggerCalledBallFeedback = useCallback((ball) => {
    if (!ball?.letter || !ball?.number) return;

    showCalledBallFlash(ball);
    speakCalledBall(ball);
  }, [showCalledBallFlash, speakCalledBall]);

  const handleTileClick = (number) => {
    if (!manualMode || isMachineRunning || winnerCalled) return;

    const letter = getLetterForNumber(number);
    const currentNumbers = generatedNumbersRef.current;
    const exists = currentNumbers.some(
      (ball) => Number(ball.number) === Number(number)
    );
    const nextBalls = exists
      ? currentNumbers.filter((ball) => Number(ball.number) !== Number(number))
      : [...currentNumbers, { letter, number: Number(number) }];
    const latestRemainingBall = nextBalls[nextBalls.length - 1];
    const nextCurrentBall = latestRemainingBall
      ? `${latestRemainingBall.letter || getLetterForNumber(Number(latestRemainingBall.number))}${Number(latestRemainingBall.number)}`
      : null;

    if (!exists && Number(nextBallRef.current?.number) === Number(number)) {
      nextBallRef.current = null;
      setNextBall(null);
    }

    generatedNumbersRef.current = nextBalls;
    setGeneratedNumbers(nextBalls);
    setCurrentBall(nextCurrentBall);
    setAnimatingNumber(null);

    // Persist immediately so all tabs for this event update in real time
    persistCallerRuntime(
      nextBalls,
      nextCurrentBall,
      nextBallRef.current
    );
    appendAuditEvent({
      eventId: sessionDocId,
      game: gameSchedule[activeGameIndex] || {
        id: `game-${gameNumber}`,
        gameNumber,
        name: `Game ${gameNumber}`,
        pattern: selectedPattern,
        prizeAmount,
      },
      type: exists ? 'BALL_UNDONE' : 'BALL_CALLED',
      payload: {
        ball: `${letter}${number}`,
        callNumber: nextBalls.length,
        source: 'manual-tile',
      },
    });
  };

  const undoLastBall = async () => {
    stopAutoCall();
    if (isMachineRunning || generatedNumbers.length === 0) return;

    pendingCallerRuntimeRef.current = null;

    const previousBalls = generatedNumbers;
    const previousCurrentBall = currentBall;
    const previousSchedule = gameSchedule;
    const removedBall = generatedNumbers[generatedNumbers.length - 1];
    const nextBalls = generatedNumbers.slice(0, -1);
    const previousBall = nextBalls[nextBalls.length - 1];
    const nextCurrentBall = previousBall
      ? `${previousBall.letter || getLetterForNumber(previousBall.number)}${previousBall.number}`
      : null;
    const nextSchedule = gameSchedule.map((game, index) =>
      index === activeGameIndex && game.status !== 'completed'
        ? {
            ...game,
            generatedNumbers: nextBalls,
            currentBall: nextCurrentBall,
          }
        : game
    );

    // Keep the announcement comparison aligned with the shortened history.
    // The existing effect announces only when this list grows.
    const undoHistoryKey = `history:${nextBalls.map((ball) => Number(ball.number)).join(',')}`;
    pendingUndoRef.current = undoHistoryKey;
    previousGeneratedNumbersRef.current = nextBalls;
    generatedNumbersRef.current = nextBalls;
    setGeneratedNumbers(nextBalls);
    setCurrentBall(nextCurrentBall);
    setGameSchedule(nextSchedule);
    setAnimatingNumber(null);
    setCalledBallOverlay(null);
    setUndoMessage(
      `${removedBall.letter || getLetterForNumber(removedBall.number)}${removedBall.number} removed`
    );

    if (undoMessageTimeoutRef.current) {
      clearTimeout(undoMessageTimeoutRef.current);
    }

    undoMessageTimeoutRef.current = setTimeout(() => {
      setUndoMessage('');
      undoMessageTimeoutRef.current = null;
    }, 2400);

    if (!isSessionStarted || !sessionDocId) {
      pendingUndoRef.current = null;
      return;
    }

    try {
      await setDoc(
        doc(db, 'bingoMachineEvents', sessionDocId),
        {
          generatedNumbers: nextBalls,
          currentBall: nextCurrentBall,
          gameSchedule: nextSchedule,
          lastUpdated: serverTimestamp(),
        },
        { merge: true }
      );

      appendAuditEvent({
        eventId: sessionDocId,
        game: gameSchedule[activeGameIndex] || {
          id: `game-${gameNumber}`,
          gameNumber,
          name: `Game ${gameNumber}`,
          pattern: selectedPattern,
          prizeAmount,
        },
        type: 'BALL_UNDONE',
        payload: {
          ball: `${removedBall.letter || getLetterForNumber(removedBall.number)}${removedBall.number}`,
          remainingBallCount: nextBalls.length,
          source: 'undo-last-ball',
        },
      });

      setTimeout(() => {
        if (pendingUndoRef.current === undoHistoryKey) {
          pendingUndoRef.current = null;
        }
      }, 1500);
    } catch (error) {
      pendingUndoRef.current = null;
      previousGeneratedNumbersRef.current = previousBalls;
      generatedNumbersRef.current = previousBalls;
      setGeneratedNumbers(previousBalls);
      setCurrentBall(previousCurrentBall);
      setGameSchedule(previousSchedule);
      setUndoMessage('Unable to undo the last ball. Please try again.');
      // eslint-disable-next-line no-console
      console.error('Error undoing last ball:', error);
    }
  };

  const buildEventDocId = (rawName) => rawName.trim().replace(/\//g, '-');

  const serializePattern = (pattern) => pattern.flat();
  const deserializePattern = (flat) => {
    if (!Array.isArray(flat) || flat.length !== 25) {
      return Array.from({ length: 5 }, () => Array.from({ length: 5 }, () => false));
    }
    const rows = [];
    for (let i = 0; i < 5; i += 1) {
      rows.push(flat.slice(i * 5, i * 5 + 5));
    }
    return rows;
  };

  const generatedNumberRows = Array.from(
    { length: Math.ceil(generatedNumbers.length / numbersPerRow) },
    (_, index) => generatedNumbers.slice(index * numbersPerRow, (index + 1) * numbersPerRow)
  );

  const recentCalls = generatedNumbers.slice(-5).reverse();

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user || null);
      setEventsError('');
      setEventsLoading(true);

      if (!user) {
        setUserEvents([]);
        setEventsLoading(false);
        return;
      }

      try {
        const [snapshot, templateSnapshot] = await Promise.all([
          getDocs(query(collection(db, 'bingoMachineEvents'), where('createdBy', '==', user.email))),
          getDocs(query(collection(db, 'programTemplates'), where('createdByUid', '==', user.uid))),
        ]);
        const events = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setUserEvents(events);
        const savedPrograms = templateSnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setProgramTemplates(savedPrograms);
        const requestedTemplateId = new URLSearchParams(window.location.search).get('templateId');
        if (requestedTemplateId && savedPrograms.some((item) => item.id === requestedTemplateId)) {
          setSelectedTemplateId(requestedTemplateId);
        }
      } catch (error) {
        setEventsError('Unable to load events.');
        setUserEvents([]);
        // eslint-disable-next-line no-console
        console.error('Error loading bingo events:', error);
      } finally {
        setEventsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleStartSession = async () => {
    const cleanedName = eventNameInput.trim();
    if (!cleanedName) {
      setSessionError('Please enter an event name to continue.');
      return;
    }

    if (!currentUser?.email) {
      setSessionError('You must be logged in to create a bingo event.');
      return;
    }

    setIsStartingSession(true);
    setSessionError('');

    const eventDocId = buildEventDocId(cleanedName);
    const selectedTemplate = programTemplates.find((item) => item.id === selectedTemplateId);
    const initialSchedule = selectedTemplate
      ? createEventScheduleFromTemplate(selectedTemplate.schedule)
      : [];

    try {
      await setDoc(
        doc(db, 'bingoMachineEvents', eventDocId),
        {
          eventName: cleanedName,
          eventKey: eventDocId,
          createdBy: currentUser.email,
          createdByUid: currentUser.uid,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp(),
          selectedPattern: null,
          prizeAmount: '',
          gameNumber: 1,
          generatedNumbers: [],
          currentBall: null,
          nextBall: null,
          autoCallInterval: 10,
          autoCallEnabled: false,
          autoCallNextAt: null,
          manualMode: false,
          theme,
          customPattern: serializePattern(customPattern),
          showWinner: false,
          winnerCalled: false,
          gameSchedule: initialSchedule,
          activeGameIndex: -1,
          sourceTemplateId: selectedTemplate?.id || null,
          displayControl: {
            ...DEFAULT_DISPLAY_CONTROL,
            updatedAt: serverTimestamp(),
          },
        },
        { merge: true }
      );

      setSessionDocId(eventDocId);
      setControlView('dashboard');
      setIsSessionStarted(true);
    } catch (error) {
      setSessionError('Could not start event session. Please try again.');
      // eslint-disable-next-line no-console
      console.error('Error starting bingo session:', error);
    } finally {
      setIsStartingSession(false);
    }
  };

  const handleGoToEvent = async (eventId) => {
    setSessionError('');
    setIsStartingSession(true);

    try {
      const eventRef = doc(db, 'bingoMachineEvents', eventId);
      const eventSnap = await getDoc(eventRef);

      if (eventSnap.exists()) {
        const data = eventSnap.data();

        if (!currentUser?.email || data.createdBy !== currentUser.email) {
          setSessionError('You do not have access to this event.');
          setIsStartingSession(false);
          return;
        }

        setEventNameInput(data.eventName || eventId);
        setSelectedPattern(data.selectedPattern || null);
        setPrizeAmount(data.prizeAmount || '');
        setGameNumber(Number(data.gameNumber) || 1);
        const restoredNumbers = Array.isArray(data.generatedNumbers) ? data.generatedNumbers : [];
        const restoredNextBall = normalizeStagedBall(data.nextBall, restoredNumbers);
        generatedNumbersRef.current = restoredNumbers;
        nextBallRef.current = restoredNextBall;
        setGeneratedNumbers(restoredNumbers);
        setCurrentBall(data.currentBall || null);
        setNextBall(restoredNextBall);
        const restoredInterval = Number(data.autoCallInterval) || 10;
        autoCallIntervalRef.current = restoredInterval;
        setAutoCallInterval(restoredInterval);
        autoCallEnabledRef.current = false;
        setAutoCallEnabled(false);
        setManualMode(Boolean(data.manualMode));
        setTheme(data.theme || 'standard');
        setCustomPattern(deserializePattern(data.customPattern));
        setShowWinner(false);
        setWinnerCalled(Boolean(data.winnerCalled));
        setGameSchedule(Array.isArray(data.gameSchedule) ? data.gameSchedule : []);
        setActiveGameIndex(Number.isInteger(data.activeGameIndex) ? data.activeGameIndex : -1);
        setDisplayControl(normalizeDisplayControl(data.displayControl));
      }

      setSessionDocId(eventId);
      setControlView('dashboard');
      setIsSessionStarted(true);
    } catch (error) {
      setSessionError('Unable to open event. Please try again.');
      // eslint-disable-next-line no-console
      console.error('Error opening bingo event:', error);
    } finally {
      setIsStartingSession(false);
    }
  };

  useEffect(() => {
    const requestedEventId = new URLSearchParams(window.location.search).get('eventId');
    if (!requestedEventId || !currentUser?.email || isSessionStarted) return;
    if (requestedEventOpenedRef.current === requestedEventId) return;

    requestedEventOpenedRef.current = requestedEventId;
    handleGoToEvent(requestedEventId);
    // handleGoToEvent intentionally runs once for the event requested by the schedule page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, isSessionStarted]);

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event? This cannot be undone.')) return;

    try {
      const eventSnap = await getDoc(doc(db, 'bingoMachineEvents', eventId));
      if (!eventSnap.exists()) {
        setSessionError('Event not found.');
        return;
      }

      const eventData = eventSnap.data();
      if (!currentUser?.email || eventData.createdBy !== currentUser.email) {
        setSessionError('You can only delete your own events.');
        return;
      }

      await deleteDoc(doc(db, 'bingoMachineEvents', eventId));
      setUserEvents((prev) => prev.filter((event) => event.id !== eventId));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error deleting bingo event:', error);
      setSessionError('Unable to delete event. Please try again.');
    }
  };

  const handleBackToMainPage = () => {
    stopAutoCall();
    cancelPendingCall(true);
    setIsSessionStarted(false);
    setSessionDocId('');
    setGeneratedNumbers([]);
    generatedNumbersRef.current = [];
    setCurrentBall(null);
    setIsMachineRunning(false);
    setSelectedPattern(null);
    setPrizeAmount('');
    setManualMode(false);
    setGameNumber(1);
    setShowWinner(false);
    setWinnerCalled(false);
    setGameSchedule([]);
    setActiveGameIndex(-1);
    setDisplayControl(DEFAULT_DISPLAY_CONTROL);
    setConfettiPieces([]);
    setFireworksPieces([]);
    setCustomPattern(Array.from({ length: 5 }, () => Array.from({ length: 5 }, () => false)));
    setIsPresentationMode(false);
    setControlView('dashboard');
    setCalledBallOverlay(null);
    setUndoMessage('');
    previousGeneratedNumbersRef.current = null;
  };

  useEffect(() => {
    previousGeneratedNumbersRef.current = null;
    pendingCallerRuntimeRef.current = null;
  }, [sessionDocId, isSessionStarted]);

  useEffect(() => {
    if (!isSessionStarted) {
      previousGeneratedNumbersRef.current = null;
      return;
    }

    if (previousGeneratedNumbersRef.current === null) {
      previousGeneratedNumbersRef.current = generatedNumbers;
      return;
    }

    const previousBalls = previousGeneratedNumbersRef.current;

    if (generatedNumbers.length > previousBalls.length) {
      const previousNumbers = new Set(previousBalls.map((ball) => ball.number));
      const newBall = generatedNumbers.find((ball) => !previousNumbers.has(ball.number))
        || generatedNumbers[generatedNumbers.length - 1];

      if (newBall) {
        triggerCalledBallFeedback(newBall);
      }
    }

    previousGeneratedNumbersRef.current = generatedNumbers;
  }, [generatedNumbers, isSessionStarted, triggerCalledBallFeedback]);

  useEffect(() => {
    if (!isSessionStarted || !sessionDocId) return;

    const sessionRef = doc(db, 'bingoMachineEvents', sessionDocId);
    const unsubscribe = onSnapshot(
      sessionRef,
      (snapshot) => {
        if (!snapshot.exists()) return;

        const data = snapshot.data();
        const remoteGeneratedNumbers = Array.isArray(data.generatedNumbers)
          ? data.generatedNumbers
          : [];
        const remoteNextBall = normalizeStagedBall(data.nextBall, remoteGeneratedNumbers);

        if (pendingCallerRuntimeRef.current) {
          const remoteRuntimeKey = getCallerRuntimeKey(
            remoteGeneratedNumbers,
            data.currentBall || null,
            remoteNextBall
          );

          if (remoteRuntimeKey !== pendingCallerRuntimeRef.current) {
            return;
          }

          pendingCallerRuntimeRef.current = null;
        }

        if (pendingUndoRef.current) {
          const remoteHistoryKey = `history:${remoteGeneratedNumbers
            .map((ball) => Number(ball.number))
            .join(',')}`;
          if (remoteHistoryKey !== pendingUndoRef.current) {
            return;
          }
          pendingUndoRef.current = null;
        }

        isApplyingRemoteRef.current = true;

        setEventNameInput(data.eventName || '');
        setSelectedPattern(data.selectedPattern || null);
        setPrizeAmount(data.prizeAmount || '');
        setGameNumber(Number(data.gameNumber) || 1);
        generatedNumbersRef.current = remoteGeneratedNumbers;
        nextBallRef.current = remoteNextBall;
        setGeneratedNumbers(remoteGeneratedNumbers);
        setCurrentBall(data.currentBall || null);
        setNextBall(remoteNextBall);
        const remoteAutoCallInterval = Number(data.autoCallInterval) || 10;
        autoCallIntervalRef.current = remoteAutoCallInterval;
        setAutoCallInterval(remoteAutoCallInterval);
        setManualMode(Boolean(data.manualMode));
        setTheme(data.theme || 'standard');
        setCustomPattern(deserializePattern(data.customPattern));
        setShowWinner(Boolean(data.showWinner));
        setWinnerCalled(Boolean(data.winnerCalled));
        setGameSchedule(Array.isArray(data.gameSchedule) ? data.gameSchedule : []);
        setActiveGameIndex(Number.isInteger(data.activeGameIndex) ? data.activeGameIndex : -1);
        setDisplayControl(normalizeDisplayControl(data.displayControl));

        setTimeout(() => {
          isApplyingRemoteRef.current = false;
        }, 0);
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error('Error syncing bingo session:', error);
      }
    );
    // normalizeStagedBall is a synchronous local helper. Adding it here would
    // recreate this Firestore subscription on every render.
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSessionStarted, sessionDocId]);

  useEffect(() => {
    if (!isSessionStarted || !sessionDocId) return;
    if (pendingUndoRef.current) return;
    if (isApplyingRemoteRef.current) return;

    const persistState = async () => {
      try {
        await setDoc(
          doc(db, 'bingoMachineEvents', sessionDocId),
          {
            eventName: eventNameInput.trim(),
            selectedPattern,
            prizeAmount,
            gameNumber: Number(gameNumber) || 1,
            generatedNumbers,
            currentBall,
            nextBall,
            autoCallInterval,
            manualMode,
            theme,
            customPattern: serializePattern(customPattern),
            showWinner,
            winnerCalled,
            gameSchedule: gameSchedule.map((game, index) =>
              index === activeGameIndex && game.status !== 'completed'
                ? {
                    ...game,
                    gameNumber: Number(gameNumber) || game.gameNumber || index + 1,
                    pattern: selectedPattern,
                    prizeAmount,
                    generatedNumbers,
                    currentBall,
                    nextBall,
                    customPattern: serializePattern(customPattern),
                    status: 'active',
                  }
                : game
            ),
            activeGameIndex,
            displayControl: {
              ...displayControl,
              updatedAt: serverTimestamp(),
            },
            lastUpdated: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving bingo machine state:', error);
      }
    };

    persistState();
  }, [
    isSessionStarted,
    sessionDocId,
    isMachineRunning,
    eventNameInput,
    selectedPattern,
    prizeAmount,
    gameNumber,
    generatedNumbers,
    currentBall,
    nextBall,
    autoCallInterval,
    manualMode,
    theme,
    customPattern,
    showWinner,
    winnerCalled,
    gameSchedule,
    activeGameIndex,
    displayControl,
  ]);

  const completeScheduledGame = async () => {
    if (!winnerCalled || activeGameIndex < 0 || !gameSchedule[activeGameIndex]) return;

    stopAutoCall();
    cancelPendingCall(true);

    const completedAt = Timestamp.now();
    const completedSchedule = gameSchedule.map((game, index) => {
      if (index === activeGameIndex) {
        return {
          ...game,
          gameNumber: Number(gameNumber) || game.gameNumber || index + 1,
          pattern: selectedPattern,
          prizeAmount,
          generatedNumbers,
          currentBall,
          nextBall: null,
          customPattern: serializePattern(customPattern),
          status: 'completed',
          completed: true,
          completedAt,
        };
      }

      return game.status === 'active' ? { ...game, status: 'paused' } : game;
    });

    try {
      await setDoc(
        doc(db, 'bingoMachineEvents', sessionDocId),
        {
          gameSchedule: completedSchedule,
          activeGameIndex: -1,
          showWinner: false,
          winnerCalled: false,
          nextBall: null,
          lastUpdated: serverTimestamp(),
        },
        { merge: true }
      );

      appendAuditEvent({
        eventId: sessionDocId,
        game: completedSchedule[activeGameIndex],
        type: 'GAME_COMPLETED',
        payload: {
          finalCalledBallCount: generatedNumbers.length,
          winnerCount: winnerCalled ? 1 : 0,
          finalBall: currentBall,
        },
        idempotencyKey: `game-completed-${completedSchedule[activeGameIndex]?.id || gameNumber}`,
      });

      setGameSchedule(completedSchedule);
      setActiveGameIndex(-1);
      setWinnerCalled(false);
      setShowWinner(false);
      window.location.assign(`/bingo-schedule/${encodeURIComponent(sessionDocId)}`);
    } catch (error) {
      setSessionError('Unable to complete this game. Please try again.');
      // eslint-disable-next-line no-console
      console.error('Error completing scheduled game:', error);
    }
  };

  const completedGameCount = gameSchedule.filter(
    (game) => game?.status === 'completed'
  ).length;
  const currentPatternLabel = selectedPattern
    ? getPatternLabel(selectedPattern)
    : 'Not selected';
  const currentBallLabel = currentBall || '—';
  const upcomingGame = gameSchedule.find((game, index) => (
    index > activeGameIndex && !['active', 'completed'].includes(game?.status)
  )) || (activeGameIndex < 0
    ? gameSchedule.find((game) => !['active', 'completed'].includes(game?.status))
    : null);
  const displayModeLabels = {
    live: 'Live Game',
    upNext: 'Up Next',
    intermission: 'Intermission',
    announcement: 'Announcement',
    standby: 'Standby',
  };

  const updateDisplayControl = (updates) => {
    setDisplayControl((current) => ({
      ...current,
      ...updates,
      updatedAt: Timestamp.now(),
    }));
  };

  const showIntermission = () => {
    updateDisplayControl({
      mode: 'intermission',
      intermissionMessage: displayControl.intermissionMessage.trim()
        || 'Bingo will resume shortly.',
    });
  };

  const openEventTool = (path) => {
    window.open(
      `/${path}/${encodeURIComponent(sessionDocId)}`,
      '_blank'
    );
  };

  return (
    <>
      {showIntro && (
        <div
          className={`intro-overlay ${introFading ? 'fade-out' : ''}`}
          onClick={() => setShowIntro(false)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowIntro(false); }}
        >
          <h1 className="intro-h1">Welcome to BUZZ BOARD FLA⚡H</h1>
        </div>
      )}
      {!isSessionStarted && (
        <div className="bingo-machine-container">
          <div className="event-start-card">
            <h1>BUZZ BOARD FLA⚡H</h1>
            <p>Enter your event name to start a session.</p>
            <input
              type="text"
              value={eventNameInput}
              onChange={(e) => setEventNameInput(e.target.value)}
              className="event-name-input"
              placeholder="Event name"
            />
            <div className="event-program-choice">
              <label htmlFor="event-program-template">Starting program</label>
              <select
                id="event-program-template"
                value={selectedTemplateId}
                onChange={(event) => setSelectedTemplateId(event.target.value)}
              >
                <option value="">Blank Event</option>
                {programTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({Array.isArray(template.schedule) ? template.schedule.length : 0} games)
                  </option>
                ))}
              </select>
              <button type="button" className="event-program-manage" onClick={() => window.location.assign('/program-templates')}>
                Manage Saved Programs
              </button>
            </div>
            {sessionError && <p className="session-error">{sessionError}</p>}
            <button
              type="button"
              className="generate-button"
              onClick={handleStartSession}
              disabled={isStartingSession || !eventNameInput.trim()}
            >
              {isStartingSession ? 'Starting...' : 'Start'}
            </button>
            <div className="event-list">
              <h3>Your Events</h3>
              {!currentUser && (
                <p className="event-list-note">Sign in to view saved events.</p>
              )}
              {currentUser && eventsLoading && (
                <p className="event-list-note">Loading events...</p>
              )}
              {currentUser && !eventsLoading && eventsError && (
                <p className="event-list-error">{eventsError}</p>
              )}
              {currentUser && !eventsLoading && !eventsError && userEvents.length === 0 && (
                <p className="event-list-note">No events yet.</p>
              )}
              {currentUser && !eventsLoading && !eventsError && userEvents.length > 0 && (
                <ul className="event-list-items">
                  {userEvents.map((event) => (
                    <li key={event.id} className="event-list-item">
                      <div className="event-list-name">
                        <strong>{event.eventName || event.id}</strong>
                      </div>
                      <div className="event-list-actions">
                        <button
                          type="button"
                          className="event-action-button"
                          onClick={() => handleGoToEvent(event.id)}
                        >
                          Go to Event
                        </button>
                        <button
                          type="button"
                          className="event-action-button danger"
                          onClick={() => handleDeleteEvent(event.id)}
                        >
                          Delete Event
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
      {isSessionStarted && (
      <>
      <div className="bingo-machine-container">
        {!isPresentationMode && controlView === 'dashboard' && (
          <main className="event-control-center">
            <header className="control-center-hero">
              <div>
                <span className="machine-eyebrow">BUZZBOARD FLASH</span>
                <div className="control-center-title-row">
                  <h1>{eventNameInput.trim()}</h1>
                  <span className="machine-live-badge">
                    <span className="machine-live-dot" />
                    LIVE
                  </span>
                </div>
                <p>
                  Game {gameNumber}
                  {gameSchedule.length > 0 ? ` of ${gameSchedule.length}` : ''}
                  {' · '}{currentPatternLabel}
                  {prizeAmount ? ` · $${prizeAmount}` : ''}
                </p>
              </div>
              <button
                type="button"
                className="control-center-exit"
                onClick={handleBackToMainPage}
              >
                Exit Event
              </button>
            </header>

            <section className="control-overview" aria-label="Live event overview">
              <div>
                <span>Last Called</span>
                <strong>{currentBallLabel}</strong>
              </div>
              <div>
                <span>Next Ball Up</span>
                <strong>{nextBall ? `${nextBall.letter}${nextBall.number}` : '—'}</strong>
              </div>
              <div>
                <span>Balls Called</span>
                <strong>{generatedNumbers.length} / {maxNumber}</strong>
              </div>
              <div>
                <span>Schedule</span>
                <strong>{completedGameCount} of {gameSchedule.length} complete</strong>
              </div>
            </section>

            <section className="control-center-section">
              <div className="control-center-section-heading">
                <span className="machine-eyebrow">EVENT OPERATIONS</span>
                <h2>What would you like to control?</h2>
              </div>
              <div className="control-center-grid control-center-grid-primary">
                <button
                  type="button"
                  className="control-center-card primary"
                  onClick={() => setControlView('caller')}
                >
                  <span className="control-card-icon">●</span>
                  <span>
                    <strong>Caller</strong>
                    <small>Call balls, run auto call, and manage the live game.</small>
                  </span>
                  <b>Open Caller →</b>
                </button>
                <button
                  type="button"
                  className="control-center-card"
                  onClick={() => setControlView('schedule')}
                >
                  <span className="control-card-icon">≡</span>
                  <span>
                    <strong>Game Schedule</strong>
                    <small>Review the event lineup and open the schedule manager.</small>
                  </span>
                  <b>Manage Schedule →</b>
                </button>
                <button
                  type="button"
                  className="control-center-card"
                  onClick={() => openEventTool('bingo-display')}
                >
                  <span className="control-card-icon">▦</span>
                  <span>
                    <strong>Audience Display</strong>
                    <small>Launch the synced public flashboard in a new window.</small>
                  </span>
                  <b>Launch Display ↗</b>
                </button>
                <button
                  type="button"
                  className="control-center-card"
                  onClick={() => setControlView('display')}
                >
                  <span className="control-card-icon">◫</span>
                  <span>
                    <strong>Display Control</strong>
                    <small>
                      <span className={`display-status-dot mode-${displayControl.mode}`} />
                      {displayModeLabels[displayControl.mode]} · Control what guests see.
                    </small>
                  </span>
                  <b>Manage Display →</b>
                </button>
              </div>
            </section>

            <section className="control-center-section">
              <div className="control-center-section-heading compact">
                <span className="machine-eyebrow">VERIFICATION & EVENT</span>
                <h2>Event tools</h2>
              </div>
              <div className="control-center-grid control-center-grid-utility">
                <button
                  type="button"
                  className="control-center-card utility"
                  onClick={() => openEventTool('bingo-scanner')}
                >
                  <span className="control-card-icon">□</span>
                  <span>
                    <strong>Card Scanner</strong>
                    <small>Verify a card against this event’s live calls.</small>
                  </span>
                  <b>Open Scanner ↗</b>
                </button>
                <div className="control-center-card utility control-session-card">
                  <span className="control-card-icon">⚙</span>
                  <span>
                    <strong>Event Settings</strong>
                    <small>Theme: {theme} · Mode: {manualMode ? 'Manual' : 'Automatic'}</small>
                  </span>
                  <button
                    type="button"
                    className="control-inline-action"
                    onClick={() => setControlView('caller')}
                  >
                    Edit in Caller →
                  </button>
                </div>
                <button
                  type="button"
                  className="control-center-card utility"
                  onClick={() => openEventTool('event-reports')}
                >
                  <span className="control-card-icon">▤</span>
                  <span>
                    <strong>Reports & Audit Trail</strong>
                    <small>Review game history, calls, verifications, and winners.</small>
                  </span>
                  <b>Open Reports ↗</b>
                </button>
              </div>
            </section>
          </main>
        )}

        {!isPresentationMode && controlView === 'display' && (
          <main className="event-control-center display-control-view">
            <button
              type="button"
              className="control-back-button"
              onClick={() => setControlView('dashboard')}
            >
              ← Event Control Center
            </button>

            <header className="display-control-heading">
              <span className="machine-eyebrow">DISPLAY CONTROL</span>
              <h1>Control what appears on connected audience displays.</h1>
            </header>

            <section className="display-current-status">
              <div>
                <span className="machine-eyebrow">AUDIENCE DISPLAY</span>
                <small>CURRENTLY SHOWING</small>
                <h2>
                  <span className={`display-status-dot mode-${displayControl.mode}`} />
                  {displayModeLabels[displayControl.mode]}
                </h2>
                {displayControl.mode === 'live' && (
                  <p>Game {gameNumber} · {currentPatternLabel}{prizeAmount ? ` · $${prizeAmount}` : ''}</p>
                )}
                {displayControl.mode === 'upNext' && upcomingGame && (
                  <p>
                    Game {upcomingGame.gameNumber || gameSchedule.indexOf(upcomingGame) + 1}
                    {' · '}{upcomingGame.name || getPatternLabel(upcomingGame.pattern)}
                    {upcomingGame.prizeAmount ? ` · $${upcomingGame.prizeAmount}` : ''}
                  </p>
                )}
                {displayControl.mode === 'upNext' && !upcomingGame && <p>No upcoming game available.</p>}
                {displayControl.mode === 'intermission' && (
                  <p>{displayControl.intermissionMessage || 'Bingo will resume shortly.'}</p>
                )}
                {displayControl.mode === 'announcement' && (
                  <p>
                    {displayControl.announcementTitle || 'Announcement'}
                    {displayControl.announcementMessage ? ` · ${displayControl.announcementMessage}` : ''}
                  </p>
                )}
                {displayControl.mode === 'standby' && <p>Audience displays are in standby.</p>}
              </div>
              <button type="button" onClick={() => openEventTool('bingo-display')}>
                Open Audience Display ↗
              </button>
            </section>

            <div className="display-control-layout">
              <section className="display-mode-panel">
                <div className="control-center-section-heading">
                  <span className="machine-eyebrow">DISPLAY MODES</span>
                  <h2>Choose what guests see</h2>
                </div>

                <div className="display-mode-grid">
                  <article className={`display-mode-card ${displayControl.mode === 'live' ? 'active' : ''}`}>
                    <div>
                      <span className="display-mode-kicker">LIVE GAME</span>
                      <h3>Live Flashboard</h3>
                      <p>Show the normal live bingo flashboard and current game.</p>
                    </div>
                    <button type="button" onClick={() => updateDisplayControl({ mode: 'live' })}>
                      Show Live Game
                    </button>
                  </article>

                  <article className={`display-mode-card ${displayControl.mode === 'upNext' ? 'active' : ''}`}>
                    <div>
                      <span className="display-mode-kicker">UP NEXT</span>
                      <h3>{upcomingGame?.name || 'Next Scheduled Game'}</h3>
                      <p>
                        {upcomingGame
                          ? `Game ${upcomingGame.gameNumber || gameSchedule.indexOf(upcomingGame) + 1} · ${getPatternLabel(upcomingGame.pattern)}${upcomingGame.prizeAmount ? ` · $${upcomingGame.prizeAmount}` : ''}`
                          : 'No upcoming game available.'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateDisplayControl({ mode: 'upNext' })}
                      disabled={!upcomingGame}
                    >
                      Show Up Next
                    </button>
                  </article>

                  <article className={`display-mode-card display-mode-card-form ${displayControl.mode === 'intermission' ? 'active' : ''}`}>
                    <div>
                      <span className="display-mode-kicker">INTERMISSION</span>
                      <h3>Pause the presentation</h3>
                      <p>Replace the flashboard with an intermission message.</p>
                    </div>
                    <label htmlFor="intermission-message">Intermission message</label>
                    <input
                      id="intermission-message"
                      type="text"
                      value={displayControl.intermissionMessage}
                      onChange={(event) => updateDisplayControl({ intermissionMessage: event.target.value })}
                      placeholder="Bingo will resume shortly."
                    />
                    <div className="display-preset-row">
                      {['5 Minute Intermission', '10 Minute Intermission', '15 Minute Intermission'].map((message) => (
                        <button
                          key={message}
                          type="button"
                          onClick={() => updateDisplayControl({ intermissionMessage: message })}
                        >
                          {message.replace(' Minute Intermission', ' min')}
                        </button>
                      ))}
                    </div>
                    <button type="button" className="display-mode-action" onClick={showIntermission}>
                      Show Intermission
                    </button>
                  </article>

                  <article className={`display-mode-card display-mode-card-form ${displayControl.mode === 'announcement' ? 'active' : ''}`}>
                    <div>
                      <span className="display-mode-kicker">ANNOUNCEMENT</span>
                      <h3>Hall Announcement</h3>
                      <p>Show a custom message without changing the live game.</p>
                    </div>
                    <label htmlFor="announcement-title">Announcement title</label>
                    <input
                      id="announcement-title"
                      type="text"
                      value={displayControl.announcementTitle}
                      onChange={(event) => updateDisplayControl({ announcementTitle: event.target.value })}
                      placeholder="50/50 Drawing"
                    />
                    <label htmlFor="announcement-message">Announcement message</label>
                    <textarea
                      id="announcement-message"
                      value={displayControl.announcementMessage}
                      onChange={(event) => updateDisplayControl({ announcementMessage: event.target.value })}
                      placeholder="Drawing will take place after Game 10."
                      rows="3"
                    />
                    <button
                      type="button"
                      className="display-mode-action"
                      onClick={() => updateDisplayControl({ mode: 'announcement' })}
                      disabled={!displayControl.announcementTitle.trim() && !displayControl.announcementMessage.trim()}
                    >
                      Show Announcement
                    </button>
                  </article>

                  <article className={`display-mode-card ${displayControl.mode === 'standby' ? 'active' : ''}`}>
                    <div>
                      <span className="display-mode-kicker">STANDBY</span>
                      <h3>BuzzBoard Flash Standby</h3>
                      <p>Hide game information behind a clean standby screen.</p>
                    </div>
                    <button type="button" onClick={() => updateDisplayControl({ mode: 'standby' })}>
                      Show Standby
                    </button>
                  </article>
                </div>
              </section>

              <aside className={`display-preview mode-${displayControl.mode}`}>
                <span className="machine-eyebrow">AUDIENCE PREVIEW</span>
                <div className="display-preview-screen">
                  {displayControl.mode === 'live' && (
                    <>
                      <small>LIVE GAME</small>
                      <strong>Game {gameNumber}</strong>
                      <span>{currentPatternLabel}</span>
                      {prizeAmount && <b>${prizeAmount} Prize</b>}
                    </>
                  )}
                  {displayControl.mode === 'upNext' && (
                    <>
                      <small>UP NEXT</small>
                      {upcomingGame ? (
                        <>
                          <strong>Game {upcomingGame.gameNumber || gameSchedule.indexOf(upcomingGame) + 1}</strong>
                          <span>{upcomingGame.name || getPatternLabel(upcomingGame.pattern)}</span>
                          {upcomingGame.prizeAmount && <b>${upcomingGame.prizeAmount} Prize</b>}
                        </>
                      ) : <strong>No upcoming game</strong>}
                    </>
                  )}
                  {displayControl.mode === 'intermission' && (
                    <>
                      <small>INTERMISSION</small>
                      <strong>{displayControl.intermissionMessage || 'Bingo will resume shortly.'}</strong>
                    </>
                  )}
                  {displayControl.mode === 'announcement' && (
                    <>
                      <small>ANNOUNCEMENT</small>
                      <strong>{displayControl.announcementTitle || 'Announcement'}</strong>
                      {displayControl.announcementMessage && <span>{displayControl.announcementMessage}</span>}
                    </>
                  )}
                  {displayControl.mode === 'standby' && (
                    <>
                      <small>BUZZBOARD FLASH</small>
                      <strong>Standby</strong>
                    </>
                  )}
                </div>
                <p>Preview only. The connected display will use this saved event state.</p>
              </aside>
            </div>
          </main>
        )}

        {!isPresentationMode && controlView === 'schedule' && (
          <main className="event-control-center schedule-control-view">
            <button
              type="button"
              className="control-back-button"
              onClick={() => setControlView('dashboard')}
            >
              ← Event Control Center
            </button>
            <header className="control-center-hero">
              <div>
                <span className="machine-eyebrow">GAME SCHEDULE</span>
                <h1>{eventNameInput.trim()}</h1>
                <p>{gameSchedule.length} scheduled {gameSchedule.length === 1 ? 'game' : 'games'}</p>
              </div>
              <button
                type="button"
                className="control-center-exit"
                onClick={() => openEventTool('bingo-schedule')}
              >
                Open Schedule Manager ↗
              </button>
            </header>

            <section className="schedule-control-panel">
              <div className="control-center-section-heading">
                <span className="machine-eyebrow">EVENT LINEUP</span>
                <h2>Tonight’s games</h2>
              </div>
              {gameSchedule.length === 0 ? (
                <div className="control-empty-state">
                  <strong>No games scheduled yet</strong>
                  <p>Open the schedule manager to build this event’s game lineup.</p>
                  <button type="button" onClick={() => openEventTool('bingo-schedule')}>
                    Build Schedule ↗
                  </button>
                </div>
              ) : (
                <div className="control-schedule-list">
                  {gameSchedule.map((game, index) => {
                    const status = game?.status || (index === activeGameIndex ? 'active' : 'ready');
                    return (
                      <div
                        key={game.id || `scheduled-game-${index}`}
                        className={`control-schedule-row ${index === activeGameIndex ? 'active' : ''}`}
                      >
                        <span className="control-schedule-number">{index + 1}</span>
                        <div>
                          <strong>{game.name || `Game ${game.gameNumber || index + 1}`}</strong>
                          <small>
                            {game.pattern ? getPatternLabel(game.pattern) : 'Pattern not selected'}
                            {game.prizeAmount ? ` · $${game.prizeAmount}` : ''}
                          </small>
                        </div>
                        <span className={`control-schedule-status ${status}`}>{status}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </main>
        )}

        {isPresentationMode && (
          <div className="presentation-mode-top-controls">
            {!manualMode && (
              <>
                <button
                  type="button"
                  onClick={startMachine}
                  className="presentation-start-button"
                  disabled={isMachineRunning || autoCallEnabled || generatedNumbers.length >= maxNumber || !selectedPattern || !prizeAmount}
                >
                  {isMachineRunning
                    ? 'Machine Running...'
                    : generatedNumbers.length >= maxNumber
                    ? 'All Numbers Generated'
                    : !selectedPattern || !prizeAmount || Number(gameNumber) < 1
                    ? 'Select Pattern, Prize & Game'
                    : 'Start Machine'}
                </button>
                <button
                  type="button"
                  onClick={callWinner}
                  className="presentation-winner-button"
                  disabled={isMachineRunning}
                >
                  Call Winner
                </button>
                {autoCallEnabled && (
                  <button
                    type="button"
                    onClick={stopAutoCall}
                    className="exit-presentation-button"
                  >
                    Stop Auto Call
                  </button>
                )}
                {winnerCalled && activeGameIndex >= 0 && (
                  <button
                    type="button"
                    onClick={completeScheduledGame}
                    className="presentation-winner-button"
                    disabled={isMachineRunning}
                  >
                    Complete Game
                  </button>
                )}
              </>
            )}
            <button
              type="button"
              className="exit-presentation-button"
              onClick={() => setIsPresentationMode(false)}
            >
              ← Exit Presentation Mode
            </button>
          </div>
        )}
       {!isPresentationMode && controlView === 'caller' && (
  <button
    type="button"
    className="control-back-button caller-back-button"
    onClick={() => setControlView('dashboard')}
  >
    ← Event Control Center
  </button>
)}
       {!isPresentationMode && controlView === 'caller' && (
  <div className="machine-console-header">
    <div className="machine-console-brand">
      <div className="machine-console-title-row">
        <h1>BUZZ BOARD FLA⚡H</h1>

        <span className="machine-live-badge">
          <span className="machine-live-dot" />
          LIVE
        </span>
      </div>

      <p className="active-event-name">
        {eventNameInput.trim()}
      </p>
    </div>

    <button
      type="button"
      className={`manual-toggle ${manualMode ? 'on' : 'off'}`}
      onClick={() => {
        if (!manualMode) {
          stopAutoCall();
          cancelPendingCall(false);
        }
        setManualMode((mode) => !mode);
      }}
      aria-pressed={manualMode}
    >
      <span className="manual-toggle-indicator" />

      <span>
        Manual Mode
      </span>

      <strong>
        {manualMode ? 'ON' : 'OFF'}
      </strong>
    </button>
  </div>
)}
      {!isPresentationMode && controlView === 'caller' && (
      <>
      <div className="settings machine-control-panel">

  <div className="machine-control-panel-header">
    <div>
      <span className="machine-eyebrow">
        GAME CONTROL
      </span>

      <h2>Game Setup</h2>
    </div>

    <div className="machine-game-status">
      Game {gameNumber}
    </div>
  </div>


  <div className="machine-control-grid">

    <div className="machine-field machine-field-wide">
      <label htmlFor="pattern-select">
        Winning Pattern
      </label>

      <select
        id="pattern-select"
        value={selectedPattern || ''}
        onChange={(e) => {
          setSelectedPattern(e.target.value);

          if (e.target.value === 'custom') {
            setCustomPattern(
              Array.from(
                { length: 5 },
                () =>
                  Array.from(
                    { length: 5 },
                    () => false
                  )
              )
            );
          }
        }}
        className="pattern-select"
      >
        <option value="" disabled>
          Select a Pattern
        </option>

        {bingoPatterns.map((pattern) => (
          <option key={pattern.id} value={pattern.id}>
            {pattern.name}
          </option>
        ))}
      </select>
    </div>


    <div className="machine-field">
      <label htmlFor="prize-input">
        Prize
      </label>

      <div className="machine-money-input">
        <span>$</span>

        <input
          id="prize-input"
          type="number"
          value={prizeAmount}
          onChange={(e) =>
            setPrizeAmount(e.target.value)
          }
          className="prize-input"
          placeholder="0"
        />
      </div>
    </div>


    <div className="machine-field">
      <label htmlFor="game-number-input">
        Game
      </label>

      <input
        id="game-number-input"
        type="number"
        min={1}
        value={gameNumber}
        onChange={(e) =>
          setGameNumber(
            Math.max(
              1,
              Number(e.target.value) || 1
            )
          )
        }
        className="game-number-input"
      />
    </div>


    <div className="machine-field machine-field-wide">
      <label htmlFor="theme-select">
        Flashboard Theme
      </label>

      <select
        id="theme-select"
        value={theme}
        onChange={(e) =>
          setTheme(e.target.value)
        }
        className="theme-select"
      >
        <option value="standard">
          Standard
        </option>

        <option value="spring">
          Spring
        </option>

        <option value="valentine">
          Valentine's
        </option>

        <option value="easter">
          Easter
        </option>

        <option value="christmas">
          Christmas
        </option>

        <option value="fall">
          Fall
        </option>

        <option value="summer">
          Summer
        </option>

        <option value="halloween">
          Halloween
        </option>

        <option value="ocean">
          Ocean
        </option>

        <option value="neon">
          Neon
        </option>

        <option value="patriotic">
          Patriotic
        </option>

        <option value="gold">
          Gold
        </option>

        <option value="midnight">
          Midnight
        </option>

        <option value="throwback">
          Throwback
        </option>
      </select>
    </div>

  </div>
</div>
     {/* PRIMARY GAME ACTIONS */}
{!manualMode && (
  <div className="machine-primary-actions">

    <button
      type="button"
      onClick={startMachine}
      className="generate-button machine-call-button"
      disabled={
        isMachineRunning ||
        autoCallEnabled ||
        generatedNumbers.length >= maxNumber ||
        !selectedPattern ||
        !prizeAmount
      }
    >
      <span className="machine-call-button-label">
        {isMachineRunning
          ? 'Drawing Ball'
          : autoCallEnabled
          ? 'Auto Call Active'
          : generatedNumbers.length >= maxNumber
          ? 'All Numbers Called'
          : !selectedPattern ||
            !prizeAmount ||
            Number(gameNumber) < 1
          ? 'Complete Game Setup'
          : 'Call Next Ball'}
      </span>

      {!isMachineRunning &&
        !autoCallEnabled &&
        selectedPattern &&
        prizeAmount &&
        generatedNumbers.length < maxNumber && (
          <span className="machine-call-button-arrow">
            →
          </span>
        )}
    </button>

    <div className={`machine-auto-call ${autoCallEnabled ? 'active' : ''}`}>
      <div className="machine-auto-call-heading">
        <span>AUTO CALL</span>
        <strong>
          <span className="machine-auto-call-dot" />
          {autoCallEnabled ? 'ACTIVE' : 'OFF'}
        </strong>
      </div>

      <AutoCallCountdown
        active={autoCallEnabled}
        nextCallAt={autoCallNextAt}
      />

      <div className="machine-auto-call-controls">
        <button
          type="button"
          className="machine-auto-call-toggle"
          onClick={autoCallEnabled ? stopAutoCall : startAutoCall}
          disabled={
            !autoCallEnabled && (
              isMachineRunning ||
              generatedNumbers.length >= maxNumber ||
              !selectedPattern ||
              !prizeAmount ||
              winnerCalled
            )
          }
        >
          {autoCallEnabled ? 'Pause Auto Call' : 'Start Auto Call'}
        </button>

        <label>
          Call every
          <select
            value={autoCallInterval}
            onChange={handleAutoCallIntervalChange}
          >
            {[5, 7, 10, 12, 15, 20].map((seconds) => (
              <option key={seconds} value={seconds}>
                {seconds} seconds
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>

    <div className="machine-secondary-actions">

      <button
        type="button"
        onClick={callWinner}
        className="machine-action-button machine-winner-action"
        disabled={isMachineRunning}
      >
        Call Winner
      </button>

      {winnerCalled && activeGameIndex >= 0 && (
        <button
          type="button"
          onClick={completeScheduledGame}
          className="machine-action-button machine-complete-action"
          disabled={isMachineRunning}
        >
          Complete Game
        </button>
      )}

      <button
        type="button"
        onClick={openResetConfirm}
        className="machine-action-button"
      >
        Reset Game
      </button>

      <button
        type="button"
        onClick={handleBackToMainPage}
        className="machine-action-button"
      >
        Exit Event
      </button>

    </div>
  </div>
)}


{/* MACHINE TOOLS */}
<div className="machine-tools">

  <div className="machine-tools-header">
    <span className="machine-eyebrow">
      TOOLS
    </span>
  </div>

  <div className="presentation-controls">

    <button
      type="button"
      onClick={() => setIsPresentationMode(true)}
      className="presentation-mode-button"
    >
      <span className="machine-tool-icon">
        ◉
      </span>

      Presentation
    </button>


    <button
      type="button"
      className="presentation-mode-button"
      onClick={() => {
        window.open(
          `/bingo-scanner/${encodeURIComponent(
            sessionDocId
          )}`,
          '_blank'
        );
      }}
    >
      <span className="machine-tool-icon">
        □
      </span>

      Card Scanner
    </button>


    <button
      type="button"
      className="presentation-mode-button"
      onClick={() => {
        window.open(
          `/bingo-display/${encodeURIComponent(
            sessionDocId
          )}`,
          '_blank'
        );
      }}
    >
      <span className="machine-tool-icon">
        ▦
      </span>

      Flashboard
    </button>


    <button
      type="button"
      className="presentation-mode-button"
      onClick={() => {
        window.open(
          `/bingo-schedule/${encodeURIComponent(
            sessionDocId
          )}`,
          '_blank'
        );
      }}
    >
      <span className="machine-tool-icon">
        ≡
      </span>

      Game Schedule
    </button>


    <button
      type="button"
      className="presentation-mode-button machine-undo-button"
      onClick={undoLastBall}
      disabled={isMachineRunning || generatedNumbers.length === 0}
    >
      <span className="machine-tool-icon">
        ↶
      </span>

      Undo Last Ball
    </button>


    <button
      type="button"
      onClick={() =>
        setIsNavbarVisible(
          (visible) => !visible
        )
      }
      className="presentation-mode-button navbar-inline-button"
    >
      <span className="machine-tool-icon">
        ☰
      </span>

      {isNavbarVisible
        ? 'Hide Navbar'
        : 'Show Navbar'}
    </button>

  </div>
</div>
      {showConfirm && (
        <div
          className="confirm-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            className="confirm-modal"
            style={{
              background: '#fff',
              padding: '20px',
              borderRadius: '8px',
              maxWidth: '360px',
              width: '100%',
              textAlign: 'center',
            }}
          >
            <p>Are you sure you want to reset the machine?</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '16px' }}>
              <button onClick={confirmReset} className="confirm-button">
                Confirm
              </button>
              <button onClick={closeResetConfirm} className="cancel-button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    <div className="live-machine">
  {!manualMode && (
    <div className="machine-ball-displays">
    <div
      className={`machine-display ${
        isMachineRunning
          ? 'machine-display-running'
          : ''
      }`}
    >
      <span className="machine-display-label">
        {isMachineRunning
          ? 'DRAWING'
          : currentBall
          ? 'LAST CALLED'
          : 'READY'}
      </span>

      <div className="machine-ball-stage">
        <div
          className={
            isMachineRunning
              ? 'spinning-number'
              : 'final-number'
          }
        >
          {currentBall || '—'}
        </div>
      </div>

      <span className="machine-ball-count">
        {generatedNumbers.length} of 75 called
      </span>
    </div>

    <div className={`machine-next-ball ${nextBall ? 'ready' : ''}`}>
      <span className="machine-next-ball-label">NEXT BALL UP</span>
      <strong>{nextBall ? `${nextBall.letter}${nextBall.number}` : '—'}</strong>
      <span>
        {autoCallEnabled
          ? `Auto call every ${autoCallInterval}s`
          : nextBall
          ? 'Ready for caller'
          : 'Waiting to stage'}
      </span>
    </div>
    </div>
  )}

  <div className="machine-recent-calls" aria-live="polite">
    <span className="machine-recent-calls-label">RECENT CALLS</span>
    {recentCalls.length > 0 ? (
      <div className="machine-recent-calls-list">
        {recentCalls.map((ball, index) => (
          <span
            key={`${ball.letter || getLetterForNumber(ball.number)}-${ball.number}-${index}`}
            className={`machine-recent-call ${index === 0 ? 'newest' : ''}`}
          >
            {ball.letter || getLetterForNumber(ball.number)}{ball.number}
          </span>
        ))}
      </div>
    ) : (
      <span className="machine-recent-calls-empty">No calls yet</span>
    )}
  </div>

  {undoMessage && (
    <div className="machine-undo-feedback" role="status">
      {undoMessage}
    </div>
  )}
</div>
      </>
      )}
      {(isPresentationMode || controlView === 'caller') && (
      <>
      <div className="winning-pattern">
        <h2>
          Winning Pattern: {selectedPattern && getPatternLabel(selectedPattern)}{' '}
          {prizeAmount && `| Prize: $${prizeAmount}`}
          {gameNumber && ` | Game: ${gameNumber}`}
        </h2>
        {selectedPattern && (
          <div className="bingo-card">
            <div className="bingo-card-row">
              {bingoLetters.map((letter, index) => (
                <div key={index} className="bingo-card-cell bingo-letter">
                  {letter}
                </div>
              ))}
            </div>
            {Array.from({ length: 5 }, (_, rowIndex) => (
              <div key={rowIndex} className="bingo-card-row">
                {Array.from({ length: 5 }, (_, cellIndex) => {
                  const isCenter = rowIndex === 2 && cellIndex === 2; // 3rd row, 3rd column
                  const isHighlighted = isCenter || isPatternCellActive(rowIndex, cellIndex);
                  const onClick = selectedPattern === 'custom' ? () => toggleCustomCell(rowIndex, cellIndex) : undefined;
                  return (
                    <div
                      key={cellIndex}
                      className={`bingo-card-cell ${isHighlighted ? 'highlighted' : ''} ${isCenter ? 'free-cell' : ''} ${selectedPattern === 'custom' ? 'clickable' : ''}`}
                      onClick={onClick}
                      role={selectedPattern === 'custom' ? 'button' : undefined}
                      tabIndex={selectedPattern === 'custom' ? 0 : undefined}
                      onKeyDown={selectedPattern === 'custom' ? (e) => { if (e.key === 'Enter' || e.key === ' ') toggleCustomCell(rowIndex, cellIndex); } : undefined}
                    >
                      {isCenter ? 'FREE' : (isHighlighted ? 'X' : '')}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flashboard">
        <h2>Flashboard</h2>
        <div className={`flashboard-container theme-${theme}`}>
          <div className="flashboard-grid">
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <div key={rowIndex} className="flashboard-row">
                <div className={`flashboard-cell bingo-letter letter-${bingoLetters[rowIndex]}`}>
                  {bingoLetters[rowIndex]}
                </div>
                {Array.from({ length: numbersPerRow }).map((_, colIndex) => {
                  const number = rowIndex * numbersPerRow + colIndex + 1;
                  const isHighlighted = generatedNumbers.some(
                    (ball) => Number(ball.number) === number
                  );
                  const isBlinking = animatingNumber === number;
                  return (
                    <div
                      key={number}
                      className={`flashboard-cell ${isBlinking ? 'blinking' : isHighlighted ? 'highlighted' : ''}`}
                      onClick={() => handleTileClick(number)}
                    >
                      {number}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      {calledBallOverlay && (
        <div className="called-ball-overlay" aria-hidden="true">
          <div className={`called-ball-badge letter-${calledBallOverlay.letter}`}>
            <div className="called-ball-caption">Now Calling</div>
            <div className="called-ball-value">
              <span className="called-ball-letter">{calledBallOverlay.letter}</span>
              <span className="called-ball-number">{calledBallOverlay.number}</span>
            </div>
          </div>
        </div>
      )}
      {showWinner && (
        <div className="winner-overlay" role="dialog" aria-hidden={!showWinner}>
          <div className="bingo-winner-text">BINGO!</div>
          <div className="confetti-container">
            {confettiPieces.map((p) => (
              <div
                key={p.id}
                className="confetti-piece"
                style={{ left: p.left, background: p.bg, animationDelay: p.delay, animationDuration: p.duration, transform: `rotate(${p.rotate})` }}
              />
            ))}
            {fireworksPieces.map((fw) => (
              <div
                key={fw.id}
                className="firework"
                style={{ left: fw.left, top: fw.top, animationDelay: fw.delay }}
              >
                {Array.from({ length: fw.particleCount }).map((_, idx) => {
                  const angle = (360 / fw.particleCount) * idx;
                  return (
                    <span
                      key={`${fw.id}-p-${idx}`}
                      className="particle"
                      style={{
                        background: fw.color,
                        '--angle': `${angle}deg`,
                        animationDelay: `calc(${fw.delay} + ${idx * 0.02}s)`,
                      }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="generated-numbers">
        {generatedNumbers.length > 0 && (
          <h2>Called Numbers:</h2>
        )}
        <div className="numbers">
          {generatedNumberRows.map((row, rowIndex) => (
            <div key={`generated-row-${rowIndex}`} className="numbers-row">
              {row.map((ball, ballIndex) => (
                <div key={`${ball.letter}-${ball.number}-${ballIndex}`} className={`number-ball bingo-ball ${ball.letter}`}>
                  <div className="ball-letter">{ball.letter}</div>
                  <div className="ball-number">{ball.number}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      </>
      )}
    </div>
      </>
      )}
    </>
  );
}

export default BingoMachine;
